/**
 * DealTagsSection - Seção de tags no sidebar do modal de negócio
 * 
 * Versão: 1.0.0
 * - Lista tags atribuídas ao deal
 * - Botão para adicionar/editar tags
 * - Botão de configuração para gerenciar tags do tenant
 * 
 * RELEASE: 2026-01-07
 */

import React, { useState } from 'react';
import { Plus, Settings, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDealTagAssignments, useAssignDealTags } from '@/hooks/useDealTags';
import { DealTagBadge, AssignDealTagsDialog, DealTagManagementDialog } from '@/components/crm/deals/tags';
import { usePermissions } from '@/hooks/usePermissions';

// Props do componente
interface DealTagsSectionProps {
  dealId: string;
  tenantId: string;
}

export function DealTagsSection({ dealId, tenantId }: DealTagsSectionProps) {
  // Estados dos dialogs
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [managementDialogOpen, setManagementDialogOpen] = useState(false);

  // Permissões granulares Tags de Negócio (CRM)
  const { canView, canCreate, canEdit, canDelete } = usePermissions();
  const canViewTags = canView('crm_deal_tags');
  const canCreateTags = canCreate('crm_deal_tags');
  const canEditTags = canEdit('crm_deal_tags');
  const canDeleteTags = canDelete('crm_deal_tags');

  // Query de tags atribuídas
  const { data: assignments = [], isLoading } = useDealTagAssignments(dealId);
  const assignMutation = useAssignDealTags();
  
  // Handler para remover tag individual
  const handleRemoveTag = async (tagId: string) => {
    const newTagIds = assignments
      .filter(a => a.tag_id !== tagId)
      .map(a => a.tag_id);
    
    await assignMutation.mutateAsync({
      dealId,
      tagIds: newTagIds,
      tenantId,
    });
  };
  
  return (
    <>
      <div>
        {/* Botões de ação: o título já é renderizado pelo SectionCard pai no sidebar. */}
        <div className="flex items-center justify-end mb-2">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setAssignDialogOpen(true)}
              title="Adicionar tags"
            >
              <Plus className="h-3 w-3" />
            </Button>
            {/* Botão de gerenciamento - exige permissão de visualizar Tags de Negócio */}
            {canViewTags && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setManagementDialogOpen(true)}
                title="Gerenciar tags"
              >
                <Settings className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Lista de tags */}
        <div className="min-h-[32px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : assignments.length === 0 ? (
            <button
              onClick={() => setAssignDialogOpen(true)}
              className="w-full text-xs text-muted-foreground hover:text-foreground py-2 border border-dashed rounded-md transition-colors hover:border-muted-foreground/50"
            >
              + Adicionar tag
            </button>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {assignments.map((assignment) => (
                assignment.tag && (
                  <DealTagBadge
                    key={assignment.tag_id}
                    name={assignment.tag.name}
                    color={assignment.tag.color}
                    size="sm"
                    onRemove={() => handleRemoveTag(assignment.tag_id)}
                  />
                )
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Dialogs */}
      <AssignDealTagsDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        dealId={dealId}
        tenantId={tenantId}
      />
      
      <DealTagManagementDialog
        open={managementDialogOpen}
        onOpenChange={setManagementDialogOpen}
        tenantId={tenantId}
        canCreate={canCreateTags}
        canEdit={canEditTags}
        canDelete={canDeleteTags}
      />
    </>
  );
}
